import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { presetRange, toParams } from "./definitions";

const opts = { withCredentials: true };

export const useChatbotMetrics = (enabled) => {
  const [filters, setFilters] = useState(() => ({ preset: "30d", ...presetRange("30d"), compare: true }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({});
  const [sync, setSync] = useState(null);
  const [version, setVersion] = useState(0);
  const wasRunning = useRef(false);
  const params = useMemo(() => toParams(filters), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chatbot-metrics/summary`, { ...opts, params });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't load chatbot metrics. Check the HubSpot connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  const loadOptions = useCallback(() => {
    axios.get(`${API}/chatbot-metrics/filters`, opts).then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const pollSync = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chatbot-metrics/sync-status`, opts);
      setSync(res.data);
      if (wasRunning.current && !res.data.running) {
        if (res.data.last_error) toast.error("HubSpot sync failed");
        else toast.success("Chatbot metrics synced");
        setVersion((v) => v + 1);
        loadOptions();
      }
      wasRunning.current = !!res.data.running;
    } catch {
      setSync(null);
    }
  }, [loadOptions]);

  useEffect(() => { if (enabled) load(); }, [load, version, enabled]);
  useEffect(() => { if (enabled) loadOptions(); }, [loadOptions, enabled]);
  useEffect(() => {
    if (!enabled) return undefined;
    pollSync();
    const t = setInterval(pollSync, sync?.running ? 4000 : 60000);
    return () => clearInterval(t);
  }, [pollSync, sync?.running, enabled]);

  const triggerSync = async () => {
    try {
      await axios.post(`${API}/chatbot-metrics/sync`, {}, opts);
      wasRunning.current = true;
      toast.info("Sync started — pulling the latest from HubSpot");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the sync");
    }
    pollSync();
  };

  return { filters, setFilters, params, data, loading, error, options, sync, triggerSync, version, reload: () => setVersion((v) => v + 1) };
};
